import type { CompanyData } from "@/lib/company-defaults";

// REGEL: Kanonische Kunden-Domain. Einzige Quelle — oeffentliche Links
// (Angebot, Vertrag) zeigen IMMER hierher, nie auf die aktuelle Origin
// (Inhaber, 06.10.2026: Kunden sollen nie eine Vorschau-Domain sehen).
export const SITE_URL = "https://www.snapshotbox.at";

// SHARED_BUSINESS_ID, SHARED_AREA_SERVED and STATIC_SAME_AS_EXTRA are now
// pulled from company.business_id / area_served / same_as (table:
// company_schema_settings). Keep prerender.mjs in sync — both must produce
// the same JSON-LD shape for AI/Google entity reconciliation.

const buildSameAs = (c: CompanyData): string[] => {
  const out: string[] = [];
  if (c.social_instagram) out.push(c.social_instagram.replace(/\/$/, ""));
  if (c.social_facebook) out.push(c.social_facebook.replace(/\/$/, ""));
  if (c.social_tiktok) out.push(c.social_tiktok.replace(/\/$/, ""));
  if (c.social_youtube) out.push(c.social_youtube.replace(/\/$/, ""));
  // Admin-managed extras (directory listings, sister sites). Filter out
  // empties + dedupe so a stray blank line in the admin UI never leaks.
  const extra = (c.same_as || []).map((u) => u.trim().replace(/\/$/, "")).filter(Boolean);
  return Array.from(new Set([...out, ...extra]));
};

const buildAddress = (c: CompanyData) => ({
  "@type": "PostalAddress",
  "streetAddress": c.street_address,
  "postalCode": c.postal_code,
  "addressLocality": c.city,
  "addressRegion": c.region,
  "addressCountry": c.country_code,
});

const buildAreaServed = (c: CompanyData) =>
  (c.area_served || []).map((a) => ({ "@type": a.type, "name": a.name }));

export function buildWebsiteSchema(_company: CompanyData) {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": _company.brand_name,
    "url": SITE_URL,
    "inLanguage": ["de", "en"],
    "potentialAction": {
      "@type": "SearchAction",
      "target": {
        "@type": "EntryPoint",
        "urlTemplate": `${SITE_URL}/faq?q={search_term_string}`,
      },
      "query-input": "required name=search_term_string",
    },
  };
}

function buildAggregateRating(c: CompanyData) {
  if (
    typeof c.aggregate_rating_value === "number" &&
    typeof c.aggregate_rating_count === "number" &&
    c.aggregate_rating_count > 0
  ) {
    return {
      "@type": "AggregateRating",
      "ratingValue": c.aggregate_rating_value,
      "reviewCount": c.aggregate_rating_count,
      "bestRating": 5,
      "worstRating": 1,
    };
  }
  return undefined;
}

export function buildOrganizationSchema(c: CompanyData) {
  const aggregateRating = buildAggregateRating(c);
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    "@id": `${SITE_URL}/#organization`,
    "name": c.brand_name,
    "legalName": c.legal_name,
    "url": SITE_URL,
    "logo": `${SITE_URL}/favicon.png`,
    "image": `${SITE_URL}/og-image.jpg`,
    "email": c.contact_email,
    "telephone": c.contact_phone,
    "address": buildAddress(c),
    "founder": {
      "@type": "Person",
      "name": c.founder_name,
    },
    "foundingDate": c.founding_year,
    "identifier": {
      "@type": "PropertyValue",
      "propertyID": "Firmenbuchnummer",
      "value": c.registration_number,
    },
    "areaServed": buildAreaServed(c),
    "knowsAbout": c.knows_about,
    "sameAs": buildSameAs(c),
    ...(aggregateRating ? { aggregateRating } : {}),
  };
}

export function buildLocalBusinessSchema(c: CompanyData) {
  const aggregateRating = buildAggregateRating(c);
  return {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "@id": c.business_id,
    "name": c.legal_name,
    "url": SITE_URL,
    "telephone": c.contact_phone,
    "email": c.contact_email,
    "image": `${SITE_URL}/og-image.jpg`,
    "logo": `${SITE_URL}/favicon.png`,
    "description":
      "Premium Fotobox Experiences für Hochzeiten, Geburtstage und Events in Wien, Niederösterreich und Burgenland. Fotospiegel, 360° Videobox, Audio-Gästebuch, Fotobox, Drohnenfotobox und Digitale Fotobox mit Sofortdruck und Rundum-Service.",
    "address": buildAddress(c),
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": 48.0856,
      "longitude": 16.2869,
    },
    "areaServed": buildAreaServed(c),
    "knowsAbout": c.knows_about,
    "priceRange": "€€",
    "knowsLanguage": ["de", "en"],
    "inLanguage": "de-AT",
    "openingHoursSpecification": [
      {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
        "opens": c.office_open_time,
        "closes": c.office_close_time,
      },
    ],
    "sameAs": buildSameAs(c),
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "Fotobox Services",
      "itemListElement": buildOfferList(),
    },
    ...(aggregateRating ? { aggregateRating } : {}),
  };
}

/**
 * Build a standalone JSON-LD payload of individual `Review` items linked to
 * the LocalBusiness. Use on the homepage testimonial section so the actual
 * customer quotes (not just the aggregate rating) become machine-readable.
 *
 * Only call with VERIFIED real reviews. Author names should be the public
 * reviewer identifier (typically first name + last initial).
 */
export function buildReviewListSchema(
  reviews: Array<{ author: string; body: string; rating?: number }>,
) {
  if (!Array.isArray(reviews) || reviews.length === 0) return null;
  return {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "itemListElement": reviews.map((r, i) => ({
      "@type": "ListItem",
      "position": i + 1,
      "item": {
        "@type": "Review",
        "reviewBody": r.body,
        "author": { "@type": "Person", "name": r.author },
        "reviewRating": {
          "@type": "Rating",
          "ratingValue": r.rating ?? 5,
          "bestRating": 5,
          "worstRating": 1,
        },
        "itemReviewed": { "@type": "LocalBusiness", "@id": `${SITE_URL}/#localbusiness` },
      },
    })),
  };
}

// ---------------------------------------------------------------------------
// Offers are explicitly priced "on request" — no fixed price list is published.
// We expose this via `priceSpecification.PriceSpecification` with description
// "Price on request" + currency, which is the schema.org-compliant way for
// quote-based services. Google / LLM crawlers can read this as "contact for
// price" instead of treating the missing price as an indexing defect.
// ---------------------------------------------------------------------------
// Audio-Gästebuch is a physical rental item shipped to the customer (free
// shipping within AT, return via prepaid label). All other offers are
// on-site service rentals (LeaseOut) — no shipping/return policy applies,
// so we mark them with `businessFunction: LeaseOut` and drop the merchant
// listing fields (`priceCurrency`, `availability`) on the Offer root that
// would otherwise trigger Google's product-snippet validator.
type OfferDef = {
  name: string;
  description: string;
  url: string;
  /** true → Google treats as Merchant Listing (needs shipping + return). */
  shipped?: boolean;
};

const OFFERS: OfferDef[] = [
  { name: "Fotospiegel", description: "Interaktiver Spiegel mit Touchscreen, Animationen und Sofortdruck in 8 Sekunden.", url: "/produkte/fotospiegel" },
  { name: "360° Videobox", description: "Spektakuläre Slow-Motion-Videos aus jeder Perspektive mit persönlicher Betreuung.", url: "/videobox-mieten" },
  { name: "Audio-Gästebuch", description: "Persönliche Sprachnachrichten der Gäste über ein stilvolles Vintage-Telefon.", url: "/audio-gaestebuch", shipped: true },
  { name: "Fotobox", description: "Hochwertige Fotos mit Sofortdruck, integriertem Videodisplay und Außenflächen im Corporate Design.", url: "/produkte/fotobox" },
  { name: "Drohnenfotobox", description: "Videos aus der Vogelperspektive per automatisierter Indoor-Drohne.", url: "/drohnenfotobox-mieten" },
  { name: "Digitale Fotobox", description: "Die Fotobox am Smartphone der Gäste für große Events und Kampagnen, optional mit Druckstation in Echtzeit.", url: "/digitale-fotobox" },
];

// Free shipping within Austria — used for the Audio-Gästebuch (rental ships
// to the customer, return via prepaid label within 14 days after the event).
const SHIPPING_DETAILS_AT_FREE = {
  "@type": "OfferShippingDetails",
  "shippingRate": {
    "@type": "MonetaryAmount",
    "value": 0,
    "currency": "EUR",
  },
  "shippingDestination": {
    "@type": "DefinedRegion",
    "addressCountry": "AT",
  },
  "deliveryTime": {
    "@type": "ShippingDeliveryTime",
    "handlingTime": { "@type": "QuantitativeValue", "minValue": 0, "maxValue": 1, "unitCode": "DAY" },
    "transitTime": { "@type": "QuantitativeValue", "minValue": 1, "maxValue": 3, "unitCode": "DAY" },
  },
};

const RETURN_POLICY_RENTAL = {
  "@type": "MerchantReturnPolicy",
  "applicableCountry": "AT",
  "returnPolicyCategory": "https://schema.org/MerchantReturnFiniteReturnWindow",
  "merchantReturnDays": 14,
  "returnMethod": "https://schema.org/ReturnByMail",
  "returnFees": "https://schema.org/FreeReturn",
};

function buildOfferList() {
  return OFFERS.map((o) => {
    const base: Record<string, unknown> = {
      "@type": "Offer",
      "name": o.name,
      "url": `${SITE_URL}${o.url}`,
      "priceSpecification": {
        "@type": "PriceSpecification",
        "priceCurrency": "EUR",
        "description": "Price on request — individuelles Angebot je nach Datum, Ort und Paket.",
      },
      "itemOffered": {
        "@type": "Service",
        "name": o.name,
        "description": o.description,
        "url": `${SITE_URL}${o.url}`,
      },
    };

    if (o.shipped) {
      // Physical product shipped to customer → satisfy Google's Merchant
      // Listing requirements (shipping + return policy).
      base["priceCurrency"] = "EUR";
      base["availability"] = "https://schema.org/InStock";
      base["shippingDetails"] = SHIPPING_DETAILS_AT_FREE;
      base["hasMerchantReturnPolicy"] = RETURN_POLICY_RENTAL;
    } else {
      // On-site rental service — explicitly mark as LeaseOut so Google does
      // not classify this as a shippable product (avoids the missing
      // shippingDetails / hasMerchantReturnPolicy warnings).
      base["businessFunction"] = "https://schema.org/LeaseOut";
      base["category"] = "Equipment rental";
    }

    return base;
  });
}

// ---------------------------------------------------------------------------
// Hero media schemas — explicit ImageObject / VideoObject for the homepage so
// search engines and LLM crawlers can attribute the brand's primary creative
// assets (OG image + the autoplay experience video).
// ---------------------------------------------------------------------------
export function heroImageSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "ImageObject",
    "@id": `${SITE_URL}/#hero-image`,
    "contentUrl": `${SITE_URL}/og-image.jpg`,
    "url": `${SITE_URL}/og-image.jpg`,
    "name": "SnapShotBox | Premium Fotobox Experiences",
    "caption": "SnapShotBox Fotobox in Aktion – Hochzeit, Firmenevent, Geburtstag.",
    "creditText": "SnapShotBox",
    "license": `${SITE_URL}/datenschutz`,
    "acquireLicensePage": `${SITE_URL}/kontakt`,
    "creator": { "@type": "Organization", "name": "SnapShotBox" },
    "copyrightHolder": { "@type": "Organization", "name": "SnapShotBox" },
  };
}

export function heroVideoSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "VideoObject",
    "@id": `${SITE_URL}/#hero-video`,
    "name": "SnapShotBox Experiences – Vorschau",
    "description":
      "Vorschau der SnapShotBox Experiences: Fotospiegel, 360° Videobox, Fotobox, Drohne und Audio-Gästebuch in echten Events.",
    "thumbnailUrl": `${SITE_URL}/og-image.jpg`,
    "uploadDate": "2026-04-01",
    "contentUrl": `${SITE_URL}/videos/experiences-preview.mp4`,
    "duration": "PT1M2S",
    "width": 360,
    "height": 540,
    "encodingFormat": "video/mp4",
    "inLanguage": "de-AT",
    "publisher": {
      "@type": "Organization",
      "name": "SnapShotBox",
      "logo": { "@type": "ImageObject", "url": `${SITE_URL}/favicon.png` },
    },
  };
}

export function serviceSchema(
  name: string,
  description: string,
  url: string
) {
  const isAi = /\b(ki|ai)\b/i.test(name) || /\b(ki|ai)\b/i.test(url);
  const additionalProperty = isAi
    ? [
        {
          "@type": "PropertyValue",
          "name": "selection",
          "value":
            "Per event individually selected, standalone, individually priced. AI features are not bundled and only available at the event if booked for it.",
        },
      ]
    : undefined;
  return {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": name,
    "description": description,
    "url": `${SITE_URL}${url}`,
    "provider": {
      "@type": "LocalBusiness",
      "@id": `${SITE_URL}/#business`,
      "name": "SnapShotBox",
    },
    "areaServed": [
      { "@type": "City", "name": "Wien" },
      { "@type": "State", "name": "Niederösterreich" },
      { "@type": "State", "name": "Burgenland" },
    ],
    ...(additionalProperty ? { additionalProperty } : {}),
  };
}

export function breadcrumbSchema(
  items: Array<{ name: string; url: string }>
) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": items.map((item, i) => ({
      "@type": "ListItem",
      "position": i + 1,
      "name": item.name,
      "item": `${SITE_URL}${item.url}`,
    })),
  };
}

export function localServiceSchema(
  cityName: string,
  description: string,
  url: string,
  geo?: { lat: number; lng: number }
) {
  return {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": `Fotobox mieten in ${cityName}`,
    "description": description,
    "url": `${SITE_URL}${url}`,
    "serviceType": "Photo Booth Rental",
    "provider": {
      "@type": "LocalBusiness",
      "@id": `${SITE_URL}/#business`,
      "name": "SnapShotBox",
    },
    "areaServed": {
      "@type": "City",
      "name": cityName,
      ...(geo ? { "geo": { "@type": "GeoCoordinates", "latitude": geo.lat, "longitude": geo.lng } } : {}),
    },
  };
}

export function eventServiceSchema(
  name: string,
  description: string,
  url: string,
  eventType: string
) {
  return {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": name,
    "description": description,
    "url": `${SITE_URL}${url}`,
    "serviceType": `Photo Booth for ${eventType}`,
    "provider": {
      "@type": "LocalBusiness",
      "@id": `${SITE_URL}/#business`,
      "name": "SnapShotBox",
    },
    "areaServed": [
      { "@type": "City", "name": "Wien" },
      { "@type": "State", "name": "Niederösterreich" },
      { "@type": "State", "name": "Burgenland" },
    ],
  };
}

export function faqPageSchema(
  items: Array<{ question: string; answer: string }>
) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": items.map((item) => ({
      "@type": "Question",
      "name": item.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": item.answer,
      },
    })),
  };
}

export function articleSchema(
  headline: string,
  description: string,
  url: string,
) {
  return {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": headline,
    "description": description,
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": `${SITE_URL}${url}`,
    },
    "author": {
      "@type": "Organization",
      "name": "SnapShotBox",
      "url": SITE_URL,
    },
    "publisher": {
      "@type": "Organization",
      "name": "SnapShotBox",
      "url": SITE_URL,
      "logo": {
        "@type": "ImageObject",
        "url": `${SITE_URL}/favicon.png`,
      },
    },
  };
}
